// Two-step sign-in: the parts with no React and no network, so test/two-factor can run
// them as they are (xano/two-factor/PUSH.md has the whole design).
//
// Every sign-in endpoint now answers one of two ways: a session, or, for an account with
// two-step sign-in on, {mfa_required: true, challenge} and no session. readSignIn is the
// one place that tells them apart, so no sign-in path can mistake a challenge for a
// session or the other way round.
import { z } from "zod";

export type SignInAnswer = { kind: "session"; token: string } | { kind: "code"; challenge: string };

/** A sign-in endpoint's answer, read strictly. Anything else is an error, never a guess. */
export function readSignIn(value: unknown): SignInAnswer {
  const v = (value && typeof value === "object" ? value : {}) as Record<string, unknown>;
  if (v.mfa_required === true) {
    if (typeof v.challenge === "string" && /^[1-9][0-9]*\.[0-9a-f]{64}$/.test(v.challenge)) return { kind: "code", challenge: v.challenge };
    throw new Error("Sign-in could not continue. Try again.");
  }
  if (typeof v.authToken === "string" && v.authToken) return { kind: "session", token: v.authToken };
  throw new Error("Sign-in did not return a session. Try again.");
}

export type CodeMode = "app" | "recovery";

/** What the person typed, as the server reads it. Spaces and dashes are ignored. */
export function cleanCode(raw: string, mode: CodeMode): string {
  const s = raw.replace(/[\s-]/g, "");
  return mode === "app" ? s : s.toLowerCase();
}

export const codeSchema = z
  .object({ mode: z.enum(["app", "recovery"]), code: z.string() })
  .superRefine(({ mode, code }, ctx) => {
    const c = cleanCode(code, mode);
    if (mode === "app" && !/^\d{6}$/.test(c)) ctx.addIssue({ code: "custom", path: ["code"], message: "Enter the 6-digit code from your app." });
    if (mode === "recovery" && !/^[a-z2-7]{12}$/.test(c)) ctx.addIssue({ code: "custom", path: ["code"], message: "Enter a recovery code, like abcd-efgh-2345." });
  });
export type CodeForm = z.infer<typeof codeSchema>;

/** The recovery codes as a file someone can keep. */
export function recoveryCodesText(codes: string[], email: string | undefined, at = new Date()): string {
  return [
    "Bloxx recovery codes" + (email ? ` for ${email}` : ""),
    `Made ${at.toISOString().slice(0, 10)}. Each code signs you in once if you lose your phone.`,
    "",
    ...codes,
    "",
  ].join("\n");
}

/** The part of an otpauth:// address a person types into an app by hand, in groups of four. */
export function secretForTyping(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}
